import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import { IconButton } from "@mui/material";

import { UIButton } from "../../../ui";
import { clx } from "../../../../utils/Helpers";

import "./DocumentViewer.scss";

const displayFileName = (doc) => {
  if (!doc) return "";
  if (doc.fileName) return doc.fileName;
  const base = (doc.name || "document").trim();
  return /\.pdf$/i.test(base) ? base : `${base}.pdf`;
};

const pathExtension = (url) => {
  if (!url) return "";
  const path = url.split("?")[0].split("#")[0];
  const i = path.lastIndexOf(".");
  return i >= 0 ? path.slice(i).toLowerCase() : "";
};

const isWordLikeUrl = (url) => {
  const ext = pathExtension(url);
  return ext === ".doc" || ext === ".docx";
};

const DocumentViewer = ({
  document: doc,
  messagesBundle,
  lang,
  onClose,
  embedded,
}) => {
  const isAr = lang === "ar";
  const totalPages = Math.max(1, doc?.pageCount || 1);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [doc?.url]);

  const iframeSrc = useMemo(() => {
    if (!doc?.url) return "";
    if (totalPages <= 1) return doc.url;
    const base = doc.url.split("#")[0];
    return `${base}#page=${page}`;
  }, [doc?.url, page, totalPages]);

  const handleDownload = () => {
    if (!doc?.url) return;
    window.open(doc.url, "_blank", "noopener,noreferrer");
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;
  const wordLike = doc?.url && isWordLikeUrl(doc.url);

  return (
    <div
      className={clx("document-viewer-panel", {
        "document-viewer-panel--embedded": embedded,
      })}
    >
      {!embedded ? (
        <div className="document-viewer-head">
          <h2 className="document-viewer-title">
            {messagesBundle.documentViewer}
          </h2>
          {doc?.url ? (
            <p className="document-viewer-filename">{displayFileName(doc)}</p>
          ) : null}
        </div>
      ) : null}

      <div className="document-viewer-preview-shell">
        {doc?.url ? (
          wordLike ? (
            <div className="document-viewer-unsupported">
              <DescriptionOutlinedIcon
                sx={{ fontSize: 48, color: "#b0b7c3", mb: 1 }}
              />
              <p className="mb-3">{messagesBundle.viewerUnsupportedWord}</p>
              <UIButton
                type="button"
                variant="outlined"
                color="inherit"
                size="small"
                startIcon={<DownloadOutlinedIcon fontSize="small" />}
                onClick={handleDownload}
              >
                {messagesBundle.download}
              </UIButton>
            </div>
          ) : (
            <>
              <div className="document-viewer-chrome">
                <div
                  className="document-viewer-topbar document-viewer-topbar--iframe"
                  dir={isAr ? "rtl" : "ltr"}
                >
                  <span className="document-viewer-topbar-meta">
                    {isAr ? (
                      <>
                        <span className="document-viewer-page-num" dir="ltr">
                          {page}
                        </span>
                        {`\u00a0`}
                        {messagesBundle.viewerPageOf}
                        {`\u00a0`}
                        <span className="document-viewer-page-num" dir="ltr">
                          {totalPages}
                        </span>
                      </>
                    ) : (
                      <>
                        {page} / {totalPages}
                      </>
                    )}
                  </span>
                  <div className="document-viewer-topbar-actions">
                    <IconButton
                      type="button"
                      size="small"
                      className="document-viewer-topbar-btn"
                      onClick={handleDownload}
                      aria-label={messagesBundle.download}
                    >
                      <DownloadOutlinedIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
                <div className="document-viewer-frame-wrap">
                  <iframe
                    className="document-viewer-frame"
                    src={iframeSrc}
                    title={displayFileName(doc)}
                  />
                </div>
              </div>
              <div className="document-viewer-toolbar">
                <div
                  className="document-viewer-pagination"
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? (
                    <>
                      <IconButton
                        type="button"
                        size="small"
                        className="document-viewer-page-btn"
                        disabled={!canNext}
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        aria-label={messagesBundle.viewerNextPage}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                      <span className="document-viewer-page-current" dir="ltr">
                        {page}
                      </span>
                      <span className="document-viewer-page-of">
                        {messagesBundle.viewerPageOf}
                        {`\u00a0`}
                        <span className="document-viewer-page-num" dir="ltr">
                          {totalPages}
                        </span>
                      </span>
                      <IconButton
                        type="button"
                        size="small"
                        className="document-viewer-page-btn"
                        disabled={!canPrev}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        aria-label={messagesBundle.viewerPreviousPage}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        type="button"
                        size="small"
                        className="document-viewer-page-btn"
                        disabled={!canPrev}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        aria-label={messagesBundle.viewerPreviousPage}
                      >
                        <ChevronLeftIcon fontSize="small" />
                      </IconButton>
                      <span className="document-viewer-page-current" dir="ltr">
                        {page}
                      </span>
                      <span className="document-viewer-page-of">
                        {messagesBundle.viewerPageOf}{" "}
                        <span className="document-viewer-page-num" dir="ltr">
                          {totalPages}
                        </span>
                      </span>
                      <IconButton
                        type="button"
                        size="small"
                        className="document-viewer-page-btn"
                        disabled={!canNext}
                        onClick={() =>
                          setPage((p) => Math.min(totalPages, p + 1))
                        }
                        aria-label={messagesBundle.viewerNextPage}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </div>
                <IconButton
                  type="button"
                  size="small"
                  className="document-viewer-download-btn"
                  onClick={handleDownload}
                  aria-label={messagesBundle.download}
                >
                  <DownloadOutlinedIcon sx={{ fontSize: 24 }} />
                </IconButton>
              </div>
            </>
          )
        ) : (
          <div className="document-viewer-placeholder">
            <PictureAsPdfOutlinedIcon
              sx={{ fontSize: 48, color: "#b0b7c3", mb: 1 }}
            />
            {messagesBundle.previewPdfHint}
          </div>
        )}
      </div>

      {doc?.url && !embedded ? (
        <div className="document-viewer-foot">
          <UIButton
            type="button"
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={onClose}
          >
            {messagesBundle.closeViewer}
          </UIButton>
        </div>
      ) : null}
    </div>
  );
};

DocumentViewer.propTypes = {
  document: PropTypes.shape({
    name: PropTypes.string,
    url: PropTypes.string,
    fileName: PropTypes.string,
    pageCount: PropTypes.number,
  }),
  messagesBundle: PropTypes.object.isRequired,
  lang: PropTypes.string,
  onClose: PropTypes.func,
  embedded: PropTypes.bool,
};

DocumentViewer.defaultProps = {
  document: null,
  lang: "en",
  onClose: undefined,
  embedded: false,
};

export default DocumentViewer;
